import { useQuery } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  MeetingHeaderFragmentFragment,
  MeetingViewMeetingNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import cache from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppError from "@components/error/error";
import GraphqlError from "@components/error/graphql-error";
import Layout from "@components/layout/layout";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingQuery from "../graphql/get-meeting-query";
import { MeetingHeaderFragment } from "../graphql/get-previous-meetings-query";
import { createTopicInMeeting } from "../helpers";
import MeetingDetails from "./details";
import MeetingBreadcrumb from "./meeting-breadcrumb";
import { MeetingPageContext } from "./meeting-page-context";
import MeetingViewSidebar from "./meeting-sidebar/meeting-view-sidebar";
import MeetingTopics from "./meeting-topics";
import MeetingPageVisibilityRefreshDetection from "./meeting-visibility-refresh-detection";
import MeetingSummary from "./prep/summary";
import UndiscussedTopicsBanner from "./undiscussed-topics-banner";

const sectionSpacingClassName = (expanded: boolean) =>
  classNames(
    "!px-0 !py-0 !border-0",
    // 48px gap below an expanded section, 24px below a collapsed one.
    expanded ? "!mb-12" : "!mb-6",
  );

export const MeetingPageContent = ({
  meeting,
  meetingGroupId,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  meetingGroupId: number;
}) => {
  // Incremented to force MeetingTopics to remount (destroying Yjs providers)
  // so editors pick up notes written to the DB by the add-previous-topics mutation.
  const [topicsVersion, setTopicsVersion] = useState(0);
  const { uiPreferenceCache } = useUiPreferenceCache();
  const isExpanded = (key: keyof UiPreferenceCache) =>
    !!uiPreferenceCache[`${key}`];

  // A meeting is considered "done" once its scheduled end time has passed.
  // The Summary section only renders for ended meetings (the various summary
  // sub-states — loading, no recap, generating, etc. — are still shown).
  // Upcoming / actively-running meetings hide the section entirely since
  // there is no useful summary data yet.
  const meetingHasEnded =
    !!meeting.endDatetime && moment().isAfter(moment(meeting.endDatetime));
  const topicsExpandedKey: keyof UiPreferenceCache = meetingHasEnded
    ? "meetingPagePastTopicsExpanded"
    : "meetingPageTopicsExpanded";

  // Summary defaults to expanded on every load (local state, not
  // persisted) but stays collapsible during the session.
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  return (
    <>
      <MeetingDetails meeting={meeting} />
      <div className="flex flex-col">
        {meetingHasEnded && (
          <Layout.SidebarSection
            title="Summary"
            isExpanded={summaryExpanded}
            showExpansionControls
            onToggleExpand={setSummaryExpanded}
            className={sectionSpacingClassName(summaryExpanded)}
            titleClassName="text-sm font-medium text-gray-500"
          >
            <MeetingSummary
              meeting={meeting}
              meetingGroupId={meetingGroupId}
              isCurrentMeeting
            />
          </Layout.SidebarSection>
        )}
        {/* Notes section hidden for now */}
        <Layout.SidebarSection
          title="Topics"
          expandedUiPreferenceKey={topicsExpandedKey}
          className={sectionSpacingClassName(isExpanded(topicsExpandedKey))}
          titleClassName="text-sm font-medium text-gray-500"
          rightSide={
            meeting.canUpdate?.permission && isExpanded(topicsExpandedKey) ? (
              <Button
                className="gap-2 shrink-0"
                size={ButtonSize.medium}
                theme={buttonTheme.text}
                onClick={() => {
                  const firstTopicId = assertEdgesNonNull(meeting.topics)[0]
                    ?.id;
                  createTopicInMeeting({
                    meeting,
                    beforeTopicId: firstTopicId,
                  });
                }}
              >
                <TopicflowIcon type="plus" className="h-5 w-5" />
                Add Topic
              </Button>
            ) : null
          }
        >
          <div className="mt-2 flex flex-col gap-3">
            <UndiscussedTopicsBanner
              meeting={meeting}
              onTopicsChanged={() => setTopicsVersion((v) => v + 1)}
            />
            <MeetingTopics
              key={topicsVersion}
              meeting={meeting}
              opensTopicsInSidebar={false}
              currentPageMeeting={meeting}
            />
          </div>
        </Layout.SidebarSection>
        {/* Action Items section hidden for now */}
      </div>
    </>
  );
};

export const meetingPageRefreshInterval = 5 * 60 * 1000; // 5 minutes

const MeetingPage = ({
  meetingId,
  hideSidebar = false,
}: {
  meetingId: number;
  hideSidebar?: boolean;
}) => {
  const [sidebarMeetingView, setSidebarMeetingView] = useState<
    "overview" | "load-previous-meeting" | number
  >("overview");

  const { loading, error, data, refetch } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId },
  });

  const meeting = data?.meeting;
  const meetingGroup = meeting?.meetingGroup;
  const title = meeting?.title || meetingGroup?.title || "";
  useDocumentTitle(title);

  const hasError = error || !data || !meeting || !meetingGroup;

  // try to get data from cache so we can render meeting header asap
  const meetingCacheId = cache.identify({
    __typename: "MeetingNode",
    id: meetingId,
  });
  const meetingHeaderCache = cache.readFragment<MeetingHeaderFragmentFragment>({
    id: meetingCacheId,
    fragmentName: "MeetingHeaderFragment",
    fragment: MeetingHeaderFragment,
  });
  const meetingHeader = meeting || meetingHeaderCache;

  return (
    <>
      <MeetingPageVisibilityRefreshDetection refetch={refetch} />
      <MeetingPageContext.Provider
        value={{
          sidebarMeetingView,
          setSidebarMeetingView,
          isInSidebar: false,
          isInExtension: false,
        }}
      >
        <Layout.Header
          className="bg-[#FCFCFD]"
          rightSideClassName="gap-1 @3xl/layout-header:gap-2"
          sidebarExpandedUiPreferenceKey={
            hideSidebar ? undefined : "meetingSidebarExpanded"
          }
          title={<MeetingBreadcrumb meeting={meetingHeader} />}
        />

        <Layout.Container>
          <Layout.Main
            width="md"
            className="bg-[#FCFCFD] scrollbar-minimal"
            sidebarExpandedUiPreferenceKey={
              hideSidebar ? undefined : "meetingSidebarExpanded"
            }
          >
            {!data && loading ? (
              <div className="w-full">
                <Loading />
              </div>
            ) : error ? (
              <GraphqlError
                error={error}
                whatDidNotWork="The meeting could not be loaded."
              />
            ) : hasError ? (
              <div>
                <AppError title="The meeting could not be loaded." />
              </div>
            ) : (
              <div>
                <MeetingPageContent
                  meeting={meeting}
                  meetingGroupId={meetingGroup.id}
                />
              </div>
            )}
          </Layout.Main>
          {!hideSidebar && meeting && <MeetingViewSidebar meeting={meeting} />}
        </Layout.Container>
      </MeetingPageContext.Provider>
    </>
  );
};

export default MeetingPage;
